import { __assign, __extends, __read, __spreadArray } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { styled } from '../styles';
import { getOverrides } from '../helpers/overrides';
import { initFocusVisible } from '../utils/focusVisible';
export var isServer = typeof window === 'undefined';
var StyledAppContainer = styled('div', {});
var StyledLayersContainer = styled('div', {});
var __DEV__ = process.env.NODE_ENV !== 'production';
function defaultEventHandlerFn() {
    if (__DEV__) {
        console.warn('`LayersManager` was not found. This occurs if you are attempting to use a component requiring `Layer` without using the `BaseProvider` at the root of your app. Please visit https://baseweb.design/components/base-provider/ for more information');
    }
}
export var LayersContext = React.createContext({
    addEscapeHandler: defaultEventHandlerFn,
    removeEscapeHandler: defaultEventHandlerFn,
    addKeyDownHandler: defaultEventHandlerFn,
    removeKeyDownHandler: defaultEventHandlerFn,
    addKeyUpHandler: defaultEventHandlerFn,
    removeKeyUpHandler: defaultEventHandlerFn,
    addKeyPressHandler: defaultEventHandlerFn,
    removeKeyPressHandler: defaultEventHandlerFn,
    addDocClickHandler: defaultEventHandlerFn,
    removeDocClickHandler: defaultEventHandlerFn,
    host: undefined,
    zIndex: undefined,
});
export var Provider = LayersContext.Provider;
export var Consumer = LayersContext.Consumer;
var LayersManager = /** @class */ (function (_super) {
    __extends(LayersManager, _super);
    function LayersManager(props) {
        var _this = _super.call(this, props) || this;
        _this.host = React.createRef();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        _this.containerRef = React.createRef();
        _this.onDocumentClick = function (event) {
            var docClickHandler = _this.state.docClickHandlers[_this.state.docClickHandlers.length - 1];
            if (docClickHandler) {
                docClickHandler(event);
            }
        };
        _this.onKeyDown = function (event) {
            var keyDownHandler = _this.state.keyDownHandlers[_this.state.keyDownHandlers.length - 1];
            if (keyDownHandler) {
                keyDownHandler(event);
            }
        };
        _this.onKeyUp = function (event) {
            if (event.key === 'Escape') {
                var escapeKeyHandler = _this.state.escapeKeyHandlers[_this.state.escapeKeyHandlers.length - 1];
                if (escapeKeyHandler) {
                    escapeKeyHandler();
                }
            }
            var keyUpHandler = _this.state.keyUpHandlers[_this.state.keyUpHandlers.length - 1];
            if (keyUpHandler) {
                keyUpHandler(event);
            }
        };
        _this.onKeyPress = function (event) {
            var keyPressHandler = _this.state.keyPressHandlers[_this.state.keyPressHandlers.length - 1];
            if (keyPressHandler) {
                keyPressHandler(event);
            }
        };
        _this.onAddEscapeHandler = function (escapeKeyHandler) {
            _this.setState(function (prev) { return ({ escapeKeyHandlers: __spreadArray(__spreadArray([], __read(prev.escapeKeyHandlers), false), [escapeKeyHandler], false) }); });
        };
        _this.onRemoveEscapeHandler = function (escapeKeyHandler) {
            _this.setState(function (prev) { return ({
                escapeKeyHandlers: prev.escapeKeyHandlers.filter(function (handler) { return handler !== escapeKeyHandler; }),
            }); });
        };
        _this.onAddKeyDownHandler = function (keyDownHandler) {
            _this.setState(function (prev) { return ({ keyDownHandlers: __spreadArray(__spreadArray([], __read(prev.keyDownHandlers), false), [keyDownHandler], false) }); });
        };
        _this.onRemoveKeyDownHandler = function (keyDownHandler) {
            _this.setState(function (prev) { return ({
                keyDownHandlers: prev.keyDownHandlers.filter(function (handler) { return handler !== keyDownHandler; }),
            }); });
        };
        _this.onAddKeyUpHandler = function (keyUpHandler) {
            _this.setState(function (prev) { return ({ keyUpHandlers: __spreadArray(__spreadArray([], __read(prev.keyUpHandlers), false), [keyUpHandler], false) }); });
        };
        _this.onRemoveKeyUpHandler = function (keyUpHandler) {
            _this.setState(function (prev) { return ({
                keyUpHandlers: prev.keyUpHandlers.filter(function (handler) { return handler !== keyUpHandler; }),
            }); });
        };
        _this.onAddKeyPressHandler = function (keyPressHandler) {
            _this.setState(function (prev) { return ({ keyPressHandlers: __spreadArray(__spreadArray([], __read(prev.keyPressHandlers), false), [keyPressHandler], false) }); });
        };
        _this.onRemoveKeyPressHandler = function (keyPressHandler) {
            _this.setState(function (prev) { return ({
                keyPressHandlers: prev.keyPressHandlers.filter(function (handler) { return handler !== keyPressHandler; }),
            }); });
        };
        _this.onAddDocClickHandler = function (docClickHandler) {
            _this.setState(function (prev) { return ({ docClickHandlers: __spreadArray(__spreadArray([], __read(prev.docClickHandlers), false), [docClickHandler], false) }); });
        };
        _this.onRemoveDocClickHandler = function (docClickHandler) {
            _this.setState(function (prev) { return ({
                docClickHandlers: prev.docClickHandlers.filter(function (handler) { return handler !== docClickHandler; }),
            }); });
        };
        _this.state = {
            escapeKeyHandlers: [],
            keyDownHandlers: [],
            keyUpHandlers: [],
            keyPressHandlers: [],
            docClickHandlers: [],
        };
        return _this;
    }
    LayersManager.prototype.componentDidMount = function () {
        this.forceUpdate();
        initFocusVisible(this.containerRef.current);
        if (!isServer) {
            document.addEventListener('keydown', this.onKeyDown);
            document.addEventListener('keyup', this.onKeyUp);
            document.addEventListener('keypress', this.onKeyPress);
            // using mousedown event so that callback runs before events on children inside of the layer
            document.addEventListener('mousedown', this.onDocumentClick);
        }
    };
    LayersManager.prototype.componentWillUnmount = function () {
        if (!isServer) {
            document.removeEventListener('keydown', this.onKeyDown);
            document.removeEventListener('keyup', this.onKeyUp);
            document.removeEventListener('keypress', this.onKeyPress);
            document.removeEventListener('mousedown', this.onDocumentClick);
        }
    };
    LayersManager.prototype.render = function () {
        var _this = this;
        var _a = this.props.overrides, overrides = _a === void 0 ? {} : _a;
        var _b = __read(getOverrides(overrides.AppContainer, StyledAppContainer), 2), AppContainer = _b[0], appContainerProps = _b[1];
        var _c = __read(getOverrides(overrides.LayersContainer, StyledLayersContainer), 2), LayersContainer = _c[0], layersContainerProps = _c[1];
        return (_jsx(Consumer, { children: function (_a) {
                var host = _a.host;
                if (__DEV__) {
                    if (host !== undefined) {
                        // eslint-disable-next-line no-console
                        console.warn('There is a LayersManager already exists in your application. It is not recommended to have more than one LayersManager in an application.');
                    }
                }
                return (_jsxs(Provider, __assign({ value: {
                        host: host || _this.host.current,
                        zIndex: _this.props.zIndex,
                        addEscapeHandler: _this.onAddEscapeHandler,
                        removeEscapeHandler: _this.onRemoveEscapeHandler,
                        addKeyDownHandler: _this.onAddKeyDownHandler,
                        removeKeyDownHandler: _this.onRemoveKeyDownHandler,
                        addKeyUpHandler: _this.onAddKeyUpHandler,
                        removeKeyUpHandler: _this.onRemoveKeyUpHandler,
                        addKeyPressHandler: _this.onAddKeyPressHandler,
                        removeKeyPressHandler: _this.onRemoveKeyPressHandler,
                        addDocClickHandler: _this.onAddDocClickHandler,
                        removeDocClickHandler: _this.onRemoveDocClickHandler,
                    } }, { children: [_jsx(AppContainer, __assign({}, appContainerProps, { ref: _this.containerRef }, { children: _this.props.children }), void 0), _jsx(LayersContainer, __assign({}, layersContainerProps, { ref: _this.host }), void 0)] }), void 0));
            } }, void 0));
    };
    return LayersManager;
}(React.Component));
export default LayersManager;
